import { motion } from 'framer-motion';

/**
 * BrandMark — the Window Wizards mountain + squeegee mark.
 */
export function BrandMark({ className = '' }) {
  return (
    <img
      src="/images/ww-mark.png"
      alt=""
      aria-hidden
      className={`object-contain ${className}`}
    />
  );
}

/**
 * Wordmark — uppercase "WINDOW WIZARDS" with optional gold tagline.
 */
export function Wordmark({ className = '', size = 'md', tagline = false }) {
  const sizes = {
    sm: 'text-[13px] lg:text-[15px] tracking-[0.2em]',
    md: 'text-[15px] lg:text-[18px] tracking-[0.22em]',
    lg: 'text-[20px] lg:text-[24px] tracking-[0.22em]',
  };
  return (
    <span
      className={`relative inline-block leading-none ${
        tagline ? 'pb-[0.95em]' : ''
      } ${className}`}
    >
      <span className={`block font-heading font-light uppercase whitespace-nowrap ${sizes[size]}`}>
        Window Wizards
      </span>
      {tagline && (
        <svg
          viewBox="0 0 240 11"
          preserveAspectRatio="xMinYMid meet"
          style={{ width: 'calc(100% - 0.24em)' }}
          className="absolute left-0 bottom-0 h-auto"
          role="img"
          aria-label="Commercial and Residential Window Care"
        >
          <text
            x="0"
            y="8"
            textLength="240"
            lengthAdjust="spacing"
            fontSize="5.6"
            className="font-body uppercase fill-[hsl(var(--gold))]"
          >
            Commercial and Residential Window Care
          </text>
        </svg>
      )}
    </span>
  );
}

/**
 * Sparkle — small 4-point star that gently pulses.
 */
export function Sparkle({ className = '', style }) {
  return (
    <motion.svg
      viewBox="0 0 12 12"
      className={className}
      style={style}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{
        opacity: [0, 1, 0.4, 1, 0],
        scale: [0.6, 1, 0.9, 1.05, 0.6],
      }}
      transition={{
        duration: 3.6,
        repeat: Infinity,
        repeatDelay: 1.4,
        ease: 'easeInOut',
      }}
      fill="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="sp-gold" x1="0" y1="0" x2="12" y2="12" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#F6E6A5" />
          <stop offset="100%" stopColor="#B08A38" />
        </linearGradient>
      </defs>
      <path d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z" fill="url(#sp-gold)" />
    </motion.svg>
  );
}

/**
 * Stardust — a scattered cluster of tiny sparkles for headings/hero.
 */
export function Stardust({ className = '' }) {
  const dots = [
    { top: '6%', left: '4%', size: 6, delay: 0.2 },
    { top: '22%', left: '92%', size: 8, delay: 1.2 },
    { top: '78%', left: '10%', size: 5, delay: 0.6 },
    { top: '86%', left: '78%', size: 7, delay: 2.1 },
    { top: '42%', left: '56%', size: 4, delay: 1.8 },
  ];
  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden>
      {dots.map((d, i) => (
        <Sparkle
          key={i}
          className="absolute"
          style={{
            top: d.top,
            left: d.left,
            width: d.size,
            height: d.size,
            animationDelay: `${d.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
